import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import AthleteAvatar from './AthleteAvatar';
import { isUnderConsentAge, CONSENT_AGE_YEARS } from '../../utils/consent-age';
import type { AthleteDraft } from './athlete-draft';

interface AthleteFieldsProps {
  value: AthleteDraft;
  onChange: (next: AthleteDraft) => void;
  /** Distinct per athlete: a page can have several of these at once. */
  idPrefix: string;
  /** Given, the athlete can be taken off the list again. */
  onRemove?: () => void;
  /**
   * Ask the gender of the class they compete in too — male or female, or left
   * unsaid, when the entry dialog asks it instead.
   */
  withGender?: boolean;
  /**
   * The gender has to be given: the guided sign-up enters the child at once,
   * so there is no later dialog to ask it.
   */
  genderRequired?: boolean;
}

/**
 * What we ask about a child being entered: a name, a surname and a date of
 * birth — and the colour their avatar is drawn in, which is the parent's
 * choice and the only decoration a managed account has.
 *
 * The date of birth is not a formality: it is the class they will run in, and
 * it is what says whether there is an account for us to hold at all. From 14 a
 * person consents for themselves, so the form says so as soon as the date
 * crosses that line rather than letting the server refuse it afterwards (see
 * WardCreation).
 *
 * The gender of the class they compete in is asked where the form says so:
 * optional when correcting a child, required on the guided sign-up, which
 * enters the child at once.
 */
export default function AthleteFields({
  value,
  onChange,
  idPrefix,
  onRemove,
  withGender = false,
  genderRequired = false,
}: AthleteFieldsProps) {
  const { t } = useTranslation();
  const set = (changes: Partial<AthleteDraft>) => onChange({ ...value, ...changes });

  const tooOld = value.birth_date.length === 10 && !isUnderConsentAge(value.birth_date);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-3" data-testid="athlete-fields">
      <div className="flex items-start gap-3">
        <AthleteAvatar
          name={value.name}
          surname={value.surname}
          color={value.avatar_color}
          onColorChange={(color) => set({ avatar_color: color })}
        />
        <div className="grid flex-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-name`}>{t('profile.name')}</Label>
            <Input
              id={`${idPrefix}-name`}
              value={value.name}
              onChange={(event) => set({ name: event.target.value })}
              autoComplete="off"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-surname`}>{t('profile.surname')}</Label>
            <Input
              id={`${idPrefix}-surname`}
              value={value.surname}
              onChange={(event) => set({ surname: event.target.value })}
              autoComplete="off"
              required
            />
          </div>
        </div>
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            aria-label={t('guardian.picker.removeAthlete')}
          >
            <Trash2 className="size-4" />
          </Button>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-birth-date`}>{t('guardian.picker.birthDate')}</Label>
        <Input
          id={`${idPrefix}-birth-date`}
          type="date"
          max={today}
          value={value.birth_date}
          onChange={(event) => set({ birth_date: event.target.value })}
          required
        />
        {tooOld ? (
          <p className="text-xs text-destructive" data-testid="athlete-too-old">
            {t('guardian.picker.tooOld', { years: CONSENT_AGE_YEARS })}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">{t('guardian.picker.birthDateHelp')}</p>
        )}
      </div>

      {withGender && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-gender`}>{t('guardian.picker.gender')}</Label>
          <select
            id={`${idPrefix}-gender`}
            value={value.gender ?? ''}
            onChange={(event) =>
              set({ gender: event.target.value === 'male' || event.target.value === 'female' ? event.target.value : null })
            }
            required={genderRequired}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {/* Required, the empty choice is only a prompt to pick one. */}
            <option value="" disabled={genderRequired}>
              {genderRequired ? t('guardian.picker.genderChoose') : t('guardian.picker.genderUnset')}
            </option>
            <option value="male">{t('stageEntry.dialog.male')}</option>
            <option value="female">{t('stageEntry.dialog.female')}</option>
          </select>
          <p className="text-xs text-muted-foreground">
            {genderRequired ? t('guardian.picker.genderRequiredHelp') : t('guardian.picker.genderHelp')}
          </p>
        </div>
      )}
    </div>
  );
}
